import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Typography from '@mui/material/Typography';
import type { ActionArgs, LoaderArgs, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useActionData, useFetcher, useLoaderData, useLocation, useNavigate, useNavigation } from '@remix-run/react';
import * as React from 'react';
import {
  CartProductMobile,
  getCartItemTotalQuantity,
  VariantHeaderDesktop,
  VariantMobile,
  VariantPack,
  VariantProduct,
} from '~/components/Cart';
import { ConfirmationDialog, PhoneDialog } from '~/components/Cart/common';
import { PackDetails } from '~/components/DesignLab/common';
import RelatedProducts from '~/components/Home/RelatedProducts';
import { ApproveModal } from '~/components/SendSwag/ApproveModal';
import { ShipmentSelectionModal } from '~/components/SendSwag/ShipmentSelectionModal';
import { RemixLink } from '~/components/shared/links';
import { initialPackConfig } from '~/routes/build-pack/create';
import type { TDesignLab } from '~/routes/design-lab';
import type { Attribute } from '~/services/api-utils.server';
import type {
  CartItem,
  CartItemPackPart,
  CartItemProductPart,
  ConfigurationType,
  DraftRequestPack,
  DraftRequestProduct,
  PackProduct,
} from '~/services/api.server';
import { cartApi, RELATED_PRODUCTS_LIMIT, scApi } from '~/services/api.server';
import { getUpdatedAuthentication, requireAuthentication } from '~/services/auth.server';
import { setNotification } from '~/services/cookies.server';
import { commitSession, getSession } from '~/services/session.server';
import {
  getCartItemId,
  getCartItemPartId,
  getCurrencyFormat,
  isCartItemPack,
  isCartItemProduct,
  isCustomPackOrProduct,
  isErrorResponse,
  isPackProduct,
} from '~/utils/common';
import { ConfigurationTypes } from '~/utils/constants';
import * as gtm from '~/utils/gtm.client';
import { getProductWeight, getSendSwagData } from '~/utils/sendSwag';
import { getPricesRequest, replacePackBuilderPrices } from '~/utils/shop';

export const meta: MetaFunction = () => ({
  title: 'SwagCharm | Cart',
  description: '',
  keywords: '',
});

export async function loader({ request }: LoaderArgs) {
  const { user } = await getUpdatedAuthentication(request);
  const session = await getSession(request);

  const accountId = session.get('accountId');
  const cart = cartApi.getCart(session);

  const productsCategories = [...new Set(cart.products.map(({ product }) => product.category.id))];
  const productsIds = [...new Set(cart.products.map(({ product }) => product.id))];

  const relatedProducts = await scApi.getRelatedProducts(user?.accessToken, accountId, {
    category__in: productsCategories.join(),
    exclude: productsIds.join(),
    limit: RELATED_PRODUCTS_LIMIT.toString(),
  });

  let userPhone = '';
  if (user) {
    const authId = user.profile.id!;
    userPhone = (await scApi.getUser(user.accessToken, accountId, authId))?.phone ?? '';
  }

  const canCheckout = cart.products.every(
    (p) =>
      p.configuration_type !== 'Decorated' ||
      p.product.design_status?.name === 'Approved' ||
      (p.product.is_pod && p.product.design_status?.name === 'Design Ready')
  );

  return { cart, relatedProducts: relatedProducts.results, canCheckout, userPhone, isLoggedIn: Boolean(user) };
}

export async function action({ request }: ActionArgs) {
  const [formData, session] = await Promise.all([request.formData(), getSession(request)]);

  const cart = cartApi.getCart(session);
  const action = formData.get('_action');

  if (action === 'saveDraft') {
    const { user, headers = new Headers() } = await requireAuthentication(request);

    const payloadDictionary: {
      packs: DraftRequestPack[];
      products: DraftRequestProduct[];
    } = { packs: [], products: [] };

    for (const cartItem of cart.products) {
      const product = cartItem.product;
      const isCustom = isCustomPackOrProduct(product);
      const isDecorated = cartItem.configuration_type === 'Decorated';

      if (isCartItemPack(cartItem) && isPackProduct(product)) {
        const variants = cartItem.parts.map((part) => {
          const container = part.items.find((item) => item.product_id === product.container.id);
          const containerAttributes = container?.attributes?.reduce((acc, attribute) => {
            const newAttribute = { [attribute.name]: attribute.value };
            return { ...acc, ...newAttribute };
          }, {});
          const filler = part.items.find((item) => item.product_id === product.filler?.id);
          const fillerAttributes = filler?.attributes?.reduce((acc, attribute) => {
            const newAttribute = { [attribute.name]: attribute.value };
            return { ...acc, ...newAttribute };
          }, {});

          const itemsWithoutContainer = part.items.filter((item) => item.product_id !== product.container.id);
          const itemsWithoutFiller = itemsWithoutContainer.filter((item) => item.product_id !== product.filler?.id);

          // add price and format attributes
          const itemsFormatted = itemsWithoutFiller.map((item) => {
            const product2 = product.items.find((_item) => _item.product?.id === item.product_id);

            const attributes = item.attributes?.reduce((acc, attribute) => {
              const newAttribute = { [attribute.name]: attribute.value };
              return { ...acc, ...newAttribute };
            }, {});
            return { product: item.product_id, price: product2?.product?.prices.price.amount, attributes };
          });

          return {
            quantity: part.quantity,
            price: part.price,
            container: {
              product: container?.product_id,
              price: product.container.prices.price.amount,
              attributes: containerAttributes,
            },
            ...(filler !== undefined && product.filler
              ? {
                  filler: {
                    product: filler.product_id,
                    price: product.filler.prices.price.amount,
                    attributes: fillerAttributes,
                  },
                }
              : {}),
            items: itemsFormatted,
          };
        });

        if (isDecorated && !isCustom) {
          const draftCartItem: DraftRequestPack = {
            name: product.name,
            mockup_image: cartItem.mockup_images?.[product.id] ?? product.image,
            configuration_type: cartItem.configuration_type,
            container: {
              product: product.container.id,
              configuration_type: 'Decorated',
              mockup_image: cartItem.mockup_images?.[product.container.id] ?? null,
            },
            filler: product.filler
              ? {
                  product: product.filler.id,
                  configuration_type: 'Decorated',
                  mockup_image: cartItem.mockup_images?.[product.filler.id] ?? null,
                }
              : null,
            items: product.items.map((item) => ({
              product: item.product?.id!,
              configuration_type: 'Decorated',
              units_per_pack: item.units_per_pack,
              mockup_image: cartItem.mockup_images?.[item.product?.id!] ?? null,
            })),

            variants: variants,
          };
          payloadDictionary.packs.push(draftCartItem);
        } else {
          const draftCartItem: DraftRequestPack = {
            product: product.id,
            name: product.name,
            configuration_type: cartItem.configuration_type,
            ...(cartItem.mockup_images ? cartItem.mockup_images : {}),

            variants: variants,
          };
          payloadDictionary.packs.push(draftCartItem);
        }
      } else if (isCartItemProduct(cartItem)) {
        const variants = cartItem.parts.map((part) => {
          const attributes = part.attributes?.reduce((acc, attribute) => {
            const newAttribute = { [attribute.name]: attribute.value };
            return { ...acc, ...newAttribute };
          }, {});
          return {
            price: part.price,
            quantity: part.quantity,
            attributes,
          };
        });
        const draftCartItem: DraftRequestProduct = {
          product: product.id,
          name: product.name,
          configuration_type: cartItem.configuration_type,
          mockup_image: cartItem.mockup_images?.[product.id],
          variants: variants as Array<{ attributes?: Record<string, string>; quantity: number; price: number }>,
        };

        payloadDictionary.products.push(draftCartItem);
      }
    }

    const draft = await scApi.createDraftOrder(user.accessToken, session.get('accountId'), {
      packs: payloadDictionary.packs,
      products: payloadDictionary.products,
    });

    if (!isErrorResponse(draft)) {
      cartApi.deleteAllProducts(session);
      session.set('packBuilder', initialPackConfig);
      session.unset('designLabQueue');
      session.unset('designLab');
      headers.append('Set-Cookie', await commitSession(session));
      return json({ draftOrderId: draft.id }, { headers });
    }
    setNotification(session, 'error', 'Unable to create draft order.');
    headers.append('Set-Cookie', await commitSession(session));
    return json({ errors: draft.error_reason }, { headers });
  }

  const { user, headers = new Headers() } = await getUpdatedAuthentication(request);

  if (action === 'addDesignLab') {
    const cartItemId = formData.get('cartItemId')?.toString();
    const lastPageComesFrom = formData.get('lastPageComesFrom')?.toString();
    const cartItem = cart.products.find((p) => getCartItemId(p) === cartItemId);

    if (!cartItem) {
      throw redirect('/cart', { headers });
    }

    const designLab = {
      quantity: cartItem.quantity,
      product: cartItem.product,
      items_attributes: cartItem.items_attributes ?? [],
      mockup_images: cartItem.mockup_images ?? {},
      generated_images: cartItem.generated_images ?? {},
      items_units_per_pack: {},
      items_no_colors: {},
      last_page_comes_from: lastPageComesFrom,
      from_pack_builder: cartItem.from_pack_builder ?? false,
    } as TDesignLab;
    if (isPackProduct(cartItem.product)) {
      for (const item of cartItem.product.items) {
        designLab.items_units_per_pack[item.product!.id] = item.units_per_pack;
      }
    }

    session.set('designLab', designLab);
    headers.append('Set-Cookie', await commitSession(session));
    if (isPackProduct(designLab.product)) {
      throw redirect(`/design-lab/${designLab.product.slug}`, { headers });
    } else {
      throw redirect(`/design-lab/bulk/${designLab.product.slug}`, { headers });
    }
  }

  if (action === 'addPhone') {
    if (!user) throw redirect('/cart', { headers });
    const phone = formData.get('phone')?.toString() ?? '';
    const accountId = session.get('accountId');
    const authId = session.get('sessionKey')?.profile?.id;
    const response = await scApi.updateUserPhone(user.accessToken, accountId, authId, phone);
    if (response?.phone) {
      setNotification(session, 'success', 'Phone number was added successfully');
    } else {
      setNotification(session, 'error', 'Phone number was not added');
    }
    return json(null, { headers: { 'Set-Cookie': await commitSession(session) } });
  }

  if (action === 'delete') {
    const productId = formData.get('product')?.toString() || '';
    cartApi.deleteProduct(session, productId);

    headers.append('Set-Cookie', await commitSession(session));
  } else if (action === 'deletePart') {
    const productId = formData.get('product')?.toString() || '';
    const part = formData.get('part')?.toString() || '';

    cartApi.deleteProductPart(session, productId, part);
    headers.append('Set-Cookie', await commitSession(session));
  } else if (action === 'changeQty') {
    const quantity = Number(formData.get('quantity')) || 1;
    const productId = formData.get('productId')?.toString();
    const configuration_type = (formData.get('configuration_type')?.toString() as ConfigurationType) || '';

    const partId = formData.get('part')?.toString() || '';
    const cartItemId = formData.get('cartItem')?.toString() || '';

    if (!productId) {
      throw redirect('/cart', { headers });
    }

    const cartItem = cart.products.find((p) => getCartItemId(p) === cartItemId)!;
    const product = cartItem.product as PackProduct;
    const packAttributes = cartItem.parts;

    const part: CartItemPackPart | CartItemProductPart = cartItem.parts.find(
      (part) => getCartItemPartId(cartItem, part) === partId
    )!;

    if (!packAttributes || !configuration_type) {
      throw redirect('/cart', { headers });
    }

    const accountId = session.get('accountId');

    // negative id is used when creating a pack in the pack builder
    if (cartItem.product.id < 0 && 'items' in part) {
      const container = part.items.find((item) => item.product_id === product.container.id);
      const containerAttributes = container?.attributes?.reduce((acc, attribute) => {
        const newAttribute = { [attribute.name]: attribute.value };
        return { ...acc, ...newAttribute };
      }, {});
      const filler = part.items.find((item) => item.product_id === product.filler?.id);
      const fillerAttributes = filler?.attributes?.reduce((acc, attribute) => {
        const newAttribute = { [attribute.name]: attribute.value };
        return { ...acc, ...newAttribute };
      }, {});

      const pricesRequest = {
        pack_quantity: quantity,
        calculate_breakdown: false,
        products: part.items.map((item) => {
          const attributes = item.attributes?.reduce((acc, attribute) => {
            const newAttribute = { [attribute.name]: attribute.value };
            return { ...acc, ...newAttribute };
          }, {});
          const productInCart = product.items.find((_item) => _item.id === item.product_id)!;
          return {
            product: item.product_id,
            units_per_pack: productInCart?.units_per_pack,
            attributes,
          };
        }),
      };
      if (product.container) {
        pricesRequest.products.push({
          product: product.container.id,
          units_per_pack: 1,
          attributes: containerAttributes,
        });
      }
      if (product.filler && filler !== undefined) {
        pricesRequest.products.push({
          product: product.filler.id,
          units_per_pack: 1,
          attributes: fillerAttributes,
        });
      }

      const prices = await scApi.getPackBuilderPrices(pricesRequest);
      if (!isErrorResponse(prices)) {
        replacePackBuilderPrices(product as PackProduct, prices);
      }
      const latestSession = await getSession(request);
      cartApi.addProduct(latestSession, quantity, product as PackProduct, part.items, configuration_type);
      headers.append('Set-Cookie', await commitSession(latestSession));
    } else {
      if ('items' in part) {
        const pricesRequest = getPricesRequest({ product, quantity, packAttributes: part.items, configuration_type });
        const prices = await scApi.getPrices(user?.accessToken, accountId, pricesRequest);
        product.prices.price.amount = prices.products[0].price.amount || product.prices.price.amount;

        const latestSession = await getSession(request);
        cartApi.addProduct(latestSession, quantity, product as PackProduct, part.items, configuration_type);
        headers.append('Set-Cookie', await commitSession(latestSession));
      } else {
        const pricesRequest = getPricesRequest({
          product,
          quantity,
          packAttributes: [{ product_id: cartItem.product.id, attributes: part.attributes }],
          configuration_type,
        });
        const prices = await scApi.getPrices(user?.accessToken, accountId, pricesRequest);
        product.prices.price.amount = prices.products[0].price.amount || part.price;

        const latestSession = await getSession(request);
        cartApi.addProduct(
          latestSession,
          quantity,
          product as PackProduct,
          [{ product_id: cartItem.product.id, attributes: part.attributes }],
          configuration_type
        );
        headers.append('Set-Cookie', await commitSession(latestSession));
      }
    }
  }

  if (action === 'sendSwag') {
    const shipmentType = formData.get('shipmentType');
    if (!shipmentType) return null;

    const redirectTo = `redirectTo=${encodeURIComponent('/cart')}`;

    if (shipmentType === 'oneAddress') {
      const _url = new URL(request.url);
      const url = `${_url.protocol}//${_url.host}/checkout/add-recipients?${redirectTo}`;
      const { sendSwagData, key } = getSendSwagData(url);
      sendSwagData.maxRecipients = 1;

      let id = 1;
      cart.products.forEach(({ product, parts }) => {
        const allAttributes = parts.flatMap((p) => p.attributes);
        const available_attributes = Object.fromEntries(
          Object.keys(product.available_attributes).map((key) => [key, [] as Attribute[]])
        );
        Object.keys(available_attributes).forEach((key) => {
          const singularKey = key.slice(0, -1);
          const allKeyValues = new Set(allAttributes.filter((a) => a.name === singularKey).map((a) => a.value));
          available_attributes[key] = product.available_attributes[key].filter((a) => allKeyValues.has(a.name));
        });

        sendSwagData.production_time = Math.max(sendSwagData.production_time, product.production_time);

        parts.forEach((part) => {
          sendSwagData.parts.push({
            id,
            product: product.id,
            name: product.name,
            image: product.image!,
            items: [],
            stock: part.quantity,
            storage_category: product.storage_category,
            variant: part.attributes.reduce((acc, attr) => {
              const plurarlKey = attr.name + 's';
              const availableAttribute = available_attributes[plurarlKey].find((a) => a.name === attr.value);
              acc[attr.name] = availableAttribute;
              return acc;
            }, {} as Record<string, string>),
            weight: product.weight,
            weight_unit: product.weight_unit,
            production_time: product.production_time,
          });

          sendSwagData.partsQty[id] = part.quantity;
          id++;
          const weight = getProductWeight(+product.weight * part.quantity, product.weight_unit.id);
          sendSwagData.weight += weight;
        });
      });

      session.set(key, sendSwagData);
      headers.append('Set-Cookie', await commitSession(session));
      return redirect(url, { headers });
    }
    if (shipmentType === 'multipleAddresses') {
      return redirect(`/checkout?${redirectTo}`, { headers });
    }
    if (shipmentType === 'warehouse') {
      return redirect(`/checkout/summary?${redirectTo}`, { headers });
    }
  }

  if (action === 'approveDesigns') {
    const { user, headers = new Headers() } = await requireAuthentication(request);
    const accountId = session.get('accountId');
    const productIds = formData.get('productIds')?.toString().split(',') ?? [];
    if (productIds.length > 0) {
      const response = await scApi.approveProducts(user.accessToken, accountId, productIds);
      if (!isErrorResponse(response)) {
        cart.products.forEach((product) => {
          product.product.design_status = { id: -1, name: 'Approved' };
        });
        session.set('cart', cart);
        headers.append('Set-Cookie', await commitSession(session));
        return json({ designsApproved: true }, { headers });
      }
    }
  }

  return json(null, { headers });
}

function CartItemProduct({ cartItem }: { cartItem: CartItem }) {
  const location = useLocation();
  const navigation = useNavigation();
  const removeFetcher = useFetcher();
  const isRemoving = removeFetcher.state !== 'idle' || navigation.state !== 'idle';

  return (
    <Box>
      <Box
        sx={{
          border: '1px solid #EBEBEB',
          borderRadius: '8px 8px 0 0',
          borderBottom: cartItem.configuration_type === ConfigurationTypes.sample ? '1px solid #EBEBEB' : '0',
          p: '16px',
          '& > div': { mx: 0 },
        }}
      >
        <VariantHeaderDesktop
          cartItem={cartItem}
          deleteIcon
          /* Add startDesigningBtn in future development */
          /* startDesigningBtn={
            cartItem?.configuration_type === ConfigurationTypes.decorated ? (
              <Form method="post">
                <input type="hidden" name="cartItemId" value={getCartItemId(cartItem)} />
                <input type="hidden" name="lastPageComesFrom" value={location.pathname} />
                <Button
                  type="submit"
                  name="_action"
                  value="addDesignLab"
                  variant="outlined"
                  disabled={isRemoving}
                  disableRipple
                  sx={{
                    color: 'primary.main',
                    fontSize: 15,
                    '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                  }}
                >
                  {Object.keys(cartItem.mockup_images ?? {}).length === 0 ? 'START DESIGNING' : 'EDIT DESIGN'}
                </Button>
              </Form>
            ) : undefined
          } */
        />
      </Box>
      {cartItem.configuration_type !== ConfigurationTypes.sample && (
        <PackDetails>
          <>
            {isCartItemPack(cartItem) &&
              cartItem.parts.map((variant, idx) => (
                <VariantPack
                  product={cartItem}
                  variant={variant}
                  position={idx + 1}
                  key={getCartItemPartId(cartItem, variant)}
                />
              ))}
            {isCartItemProduct(cartItem) &&
              cartItem.parts.map((variant) => (
                <VariantProduct product={cartItem} variant={variant} key={getCartItemPartId(cartItem, variant)} />
              ))}
          </>
        </PackDetails>
      )}
    </Box>
  );
}

const Puller = styled(Box)({
  width: 100,
  height: 7,
  backgroundColor: '#FAFAFA',
  borderRadius: 3,
  position: 'absolute',
  top: -15,
  left: 'calc(50% - 50px)',
});

const drawerBleeding = 194;

export default function CartIndex() {
  const actionData = useActionData<typeof action>();
  const { cart, relatedProducts, userPhone, isLoggedIn, canCheckout } = useLoaderData<typeof loader>();

  const [open, setOpen] = React.useState(false);
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const [openShipmentModal, setOpenShipmentModal] = React.useState(false);
  const [phone, setPhone] = React.useState<string>(userPhone);
  const [anchor, setAnchor] = React.useState(false);
  const [mobileCartSummary, setMobileCartSummary] = React.useState(false);
  const [productDrawer, setProductDrawer] = React.useState<CartItem | null>(null);
  const [approveModalData, setApproveModalData] = React.useState({ open: false, productIds: [] as number[] });

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const cartTotal = cart?.products?.reduce((total, cartItem) => {
    return total + getCartItemTotalQuantity(cartItem) * cartItem.product.prices.price.amount;
  }, 0);
  const cartTotalDisplay = cartTotal / 100;

  React.useEffect(() => {
    gtm.push_view_cart(cart.products, cartTotalDisplay);
  }, [cart.products, cartTotalDisplay]);

  React.useEffect(() => {
    setPhone(userPhone);
  }, [userPhone]);

  React.useEffect(() => {
    if (actionData && 'designsApproved' in actionData && actionData.designsApproved) {
      setOpenShipmentModal(true);
      setApproveModalData({ open: false, productIds: [] });
    }
  }, [actionData]);

  const handleClosePhone = () => {
    setPhone('');
    setOpen(false);
  };

  const handleOpenPhone = () => setOpen(true);

  const changePhoneFetcher = useFetcher();
  const handleSavePhone = () => {
    handleClosePhone();
    changePhoneFetcher.submit(
      { _action: 'addPhone', phone },
      {
        method: 'post',
        replace: true,
      }
    );
  };

  const submitDraftOrderFetcher = useFetcher();
  const submitDraftOrder = () => {
    submitDraftOrderFetcher.submit({ _action: 'saveDraft' }, { method: 'post', replace: true });
    setOpenConfirmation(true);
  };

  const handleOpenDetails = (cartItem: CartItem) => {
    setProductDrawer(cartItem);
    if (!anchor) {
      setAnchor(true);
    }
  };

  const handleChangePhone = (event: React.ChangeEvent<HTMLInputElement>) => setPhone(event.target.value.trim());

  const navigate = useNavigate();
  const redirectToDraftOrder = () =>
    navigate(`/dashboard/orders/drafts/${submitDraftOrderFetcher?.data.draftOrderId}?new_order=true`);

  const handleProceedToCheckout = () => {
    const podProductsInDesignReady = cart.products
      .filter((p) => p.product.is_pod && p.product.design_status?.name === 'Design Ready')
      .map((p) => p.product.id);
    if (podProductsInDesignReady.length > 0) {
      setApproveModalData({ open: true, productIds: podProductsInDesignReady });
    } else {
      setOpenShipmentModal(true);
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Typography variant="h5" sx={{ mb: { xs: 2, md: 4 }, mt: { xs: 1, md: 0 } }}>
            Your Cart{' '}
            {isMobile ? (
              `(${cart.products.length})`
            ) : (
              <Typography component="span" sx={{ fontSize: '20px', color: 'text.secondary' }}>
                ({cart.products.length})
              </Typography>
            )}
          </Typography>
          {cart.products.length === 0 ? (
            <Stack alignItems="center" my={6}>
              <Typography variant="h5">Your cart is empty</Typography>
              <Typography variant="h6" color="text.secondary" fontWeight={400} textAlign="center">
                Go to the store and discover our products!
              </Typography>
            </Stack>
          ) : isMobile ? (
            <>
              {cart.products.map((cartItem) => {
                return (
                  <CartProductMobile
                    key={getCartItemId(cartItem)}
                    cartItem={cartItem}
                    onOpenDetails={() => handleOpenDetails(cartItem)}
                  />
                );
              })}
            </>
          ) : (
            <Stack spacing={2.5}>
              {cart.products.map((cartItem) => (
                <CartItemProduct key={getCartItemId(cartItem)} cartItem={cartItem} />
              ))}
            </Stack>
          )}
        </Grid>
        {!isMobile && (
          <CartSummary
            cartTotalDisplay={cartTotalDisplay}
            phoneState={[phone, setPhone]}
            onOpenPhone={handleOpenPhone}
            submitDraftOrder={submitDraftOrder}
            openApprove={approveModalData.open}
            productsToApprove={approveModalData.productIds}
            onProceedToCheckout={handleProceedToCheckout}
            onCloseApproveModal={() => setApproveModalData({ open: false, productIds: [] })}
            openShipment={openShipmentModal}
            onCloseShipmentModal={() => setOpenShipmentModal(false)}
          />
        )}
      </Grid>

      <RelatedProducts products={relatedProducts} />
      <PhoneDialog
        open={open}
        phone={phone}
        onClose={handleClosePhone}
        onSave={handleSavePhone}
        onChange={handleChangePhone}
      />
      <ConfirmationDialog open={openConfirmation} onClose={redirectToDraftOrder} />
      {/* 
      Typescript and Remix error
      https://github.com/mui/material-ui/issues/36463
      https://github.com/remix-run/remix/issues/5615
      */}
      {isMobile && (
        <SwipeableDrawer
          anchor="bottom"
          open={mobileCartSummary}
          onClose={() => setMobileCartSummary(false)}
          onOpen={() => setMobileCartSummary(true)}
          swipeAreaWidth={drawerBleeding}
          disableSwipeToOpen={false}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            zIndex: '1301',
            ' .MuiPaper-root': {
              height: `calc(450px - ${drawerBleeding}px)`,
              overflow: 'visible',
            },
          }}
        >
          {!mobileCartSummary && (
            <Box
              sx={{
                backgroundColor: '#FAFAFA',
                width: '100%',
                zIndex: 1,
                p: '8px 24px 16px 24px',
                position: 'absolute',
                top: -drawerBleeding,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                visibility: 'visible',
                right: 0,
                left: 0,
                pointerEvents: 'all',
              }}
            >
              <Stack
                justifyContent="flex-end"
                direction="row"
                sx={{ mb: 1, gap: 0.5 }}
                onClick={() => setMobileCartSummary(true)}
              >
                <Typography variant="body1" color="primary.main">
                  Order summary
                </Typography>
                <ExpandLessIcon sx={{ color: 'primary.main' }} />
              </Stack>
              <Box sx={{ p: '0 8px', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" sx={{ color: 'text.primary', mr: '24px' }}>
                  Estimated Total
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
                  ${getCurrencyFormat(cartTotal / 100)}
                </Typography>
              </Box>

              {canCheckout ? (
                <>
                  <Button
                    onClick={handleProceedToCheckout}
                    disabled={cart.products.length === 0}
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{ mt: '8px !important' }}
                  >
                    PROCEED TO CHECKOUT
                  </Button>
                  <ShipmentSelectionModal open={openShipmentModal} onClose={() => setOpenShipmentModal(false)} />
                  <ApproveModal
                    open={approveModalData.open}
                    onClose={() => setApproveModalData({ open: false, productIds: [] })}
                    productIds={approveModalData.productIds}
                  />
                </>
              ) : isLoggedIn && !phone ? (
                <Button
                  disabled={cart.products.length === 0}
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleOpenPhone}
                  sx={{ mt: '16px !important', fontSize: '14px', fontWeight: 500, boxShadow: 'none', mb: '8px' }}
                >
                  SUBMIT DRAFT ORDER
                </Button>
              ) : (
                <Button
                  disabled={cart.products.length === 0}
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={submitDraftOrder}
                  sx={{ mt: '16px !important', fontSize: '14px', fontWeight: 500, boxShadow: 'none', mb: '8px' }}
                >
                  SUBMIT DRAFT ORDER
                </Button>
              )}
              <ContinueShoppingButton />
            </Box>
          )}
          {mobileCartSummary && (
            <Box
              sx={{
                p: 2,
                position: 'absolute',
                top: -drawerBleeding,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                visibility: 'visible',
                right: 0,
                left: 0,
                backgroundColor: '#FAFAFA',
                pointerEvents: 'all',
              }}
            >
              <Puller />
              <Stack justifyContent="flex-end" direction="row" gap={0.5} onClick={() => setMobileCartSummary(false)}>
                <Typography variant="body1" color="primary.main">
                  Less details
                </Typography>
                <ExpandMoreIcon sx={{ color: 'primary.main' }} />
              </Stack>
              <CartSummary
                cartTotalDisplay={cartTotalDisplay}
                phoneState={[phone, setPhone]}
                onOpenPhone={handleOpenPhone}
                submitDraftOrder={submitDraftOrder}
                openApprove={approveModalData.open}
                productsToApprove={approveModalData.productIds}
                onProceedToCheckout={handleProceedToCheckout}
                onCloseApproveModal={() => setApproveModalData({ open: false, productIds: [] })}
                openShipment={openShipmentModal}
                onCloseShipmentModal={() => setOpenShipmentModal(false)}
              />
            </Box>
          )}
        </SwipeableDrawer>
      )}
      <Drawer
        anchor="right"
        open={anchor}
        onClose={() => setAnchor(false)}
        sx={{ zIndex: '1301', '& .MuiDrawer-paper': { width: '100%' } }}
      >
        <VariantMobile product={productDrawer} setAnchor={setAnchor} />
      </Drawer>
    </>
  );
}

function CartSummary({
  cartTotalDisplay,
  phoneState,
  onOpenPhone,
  onProceedToCheckout,
  submitDraftOrder,
  openApprove,
  productsToApprove,
  onCloseApproveModal,
  openShipment,
  onCloseShipmentModal,
}: {
  cartTotalDisplay: number;
  phoneState: [string, React.Dispatch<React.SetStateAction<string>>];
  onOpenPhone: () => void;
  onProceedToCheckout: () => void;
  submitDraftOrder: () => void;
  openShipment: boolean;
  onCloseShipmentModal: () => void;
  openApprove: boolean;
  productsToApprove: number[];
  onCloseApproveModal: () => void;
}) {
  const { cart, canCheckout, isLoggedIn } = useLoaderData<typeof loader>();
  const [phone] = phoneState;

  return (
    <Grid item xs={12} md={4}>
      <Stack spacing={2}>
        <Typography variant="h5">Order Summary</Typography>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Number of Items
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {cart.products.length}
          </Typography>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">Subtotal</Typography>
          <Typography variant="h6">${getCurrencyFormat(cartTotalDisplay)}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">Shipping</Typography>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            Calculated at next step
          </Typography>
        </Stack>
        <Divider />

        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">Estimated Total</Typography>
          <Typography variant="h5">${getCurrencyFormat(cartTotalDisplay)}</Typography>
        </Stack>
        <Divider />

        {canCheckout ? (
          <>
            <Button
              onClick={onProceedToCheckout}
              disabled={cart.products.length === 0}
              variant="contained"
              size="large"
              sx={{ mt: '24px !important' }}
            >
              PROCEED TO CHECKOUT
            </Button>
            <ShipmentSelectionModal open={openShipment} onClose={onCloseShipmentModal} />
            <ApproveModal open={openApprove} onClose={onCloseApproveModal} productIds={productsToApprove} />
          </>
        ) : isLoggedIn && !phone ? (
          <Button
            disabled={cart.products.length === 0}
            variant="contained"
            size="large"
            fullWidth
            onClick={onOpenPhone}
            sx={{ mt: '24px !important' }}
          >
            SUBMIT DRAFT ORDER
          </Button>
        ) : (
          <Button
            disabled={cart.products.length === 0}
            variant="contained"
            size="large"
            fullWidth
            onClick={submitDraftOrder}
            sx={{ mt: '24px !important' }}
          >
            SUBMIT DRAFT ORDER
          </Button>
        )}
        <ContinueShoppingButton />
      </Stack>
    </Grid>
  );
}

function ContinueShoppingButton() {
  return (
    <Button fullWidth component={RemixLink} href="/shop" variant="outlined" size="large" sx={{ mt: '8px !important' }}>
      CONTINUE SHOPPING
    </Button>
  );
}
